/**
 * Targeting Builder
 * Centralizes targeting parameter construction for GAM ad requests.
 *
 * Always emits the mandatory attribution keys (game_id, publisher, source, format).
 * Optional keys are emitted only when supplied.
 *
 * Policy: keys must never contain PII. UA strings, IPs, and user identifiers must not be added here.
 */
export class TargetingBuilder {
    constructor(appId, publisher) {
        this.appId = appId;
        this.publisher = publisher;
    }
    /**
     * Build targeting parameters for an ad request.
     * Mandatory keys are always present; optional keys are added only if supplied.
     */
    build(format, extras = {}) {
        var _a, _b, _c;
        const t = {
            // Mandatory attribution keys
            game_id: (_a = extras.gameId) !== null && _a !== void 0 ? _a : this.appId,
            publisher: (_b = extras.publisherId) !== null && _b !== void 0 ? _b : this.publisher,
            // `source` defaults to 'sdk' (in-game traffic). The host SDK overrides
            // to 'on_site' for page-surround units so GAM reporting can split
            // revenue by context — same inventory product, different rate column.
            source: (_c = extras.source) !== null && _c !== void 0 ? _c : 'sdk',
            format,
            // Aliases / derived keys
            sdk_ad_type: format,
            is_rewarded: format === 'rewarded' ? 'true' : 'false',
        };
        // Optional keys — set only when supplied
        if (extras.placementName)
            t.placement_name = extras.placementName;
        if (extras.category)
            t.category = extras.category;
        if (extras.appId)
            t.app_id = extras.appId;
        if (extras.platform)
            t.platform = extras.platform;
        if (extras.deviceType)
            t.device_type = extras.deviceType;
        if (extras.orientation)
            t.orientation = extras.orientation;
        if (extras.rewardType)
            t.reward_type = extras.rewardType;
        if (extras.rewardAmount !== undefined)
            t.reward_amount = String(extras.rewardAmount);
        return t;
    }
    /** @see mergeVastCustParams — instance method preserved for existing call sites. */
    augmentVastUrl(vastUrl, extras) {
        return mergeVastCustParams(vastUrl, extras);
    }
    /**
     * Validate that targeting contains all mandatory keys
     * @throws Error if any mandatory key is missing
     */
    validate(targeting) {
        const required = ['game_id', 'publisher', 'source', 'format'];
        for (const key of required) {
            if (!targeting[key]) {
                throw new Error(`Missing required targeting key: ${key}`);
            }
        }
    }
}
/**
 * Append per-call targeting keys to an existing VAST URL's `cust_params`.
 *
 * The server-built VAST URL already contains the mandatory attribution keys
 * (game_id / publisher / source / format / sdk_ad_type / is_rewarded). This
 * helper merges additional keys (placement_name, category, hb_* prebid
 * targeting, etc.) into the same `cust_params` value so the IMA request
 * carries the full set without rebuilding the URL on the server per call.
 *
 * Keys already present in the URL win — we never overwrite server-set values.
 *
 * Exported as a free function (in addition to TargetingBuilder.augmentVastUrl)
 * so the host shell can merge `hb_*` prebid keys into the VAST URL without
 * instantiating a TargetingBuilder for the host page.
 */
export function mergeVastCustParams(vastUrl, extras) {
    if (!vastUrl || Object.keys(extras).length === 0)
        return vastUrl;
    try {
        const url = new URL(vastUrl);
        const existing = url.searchParams.get('cust_params') || '';
        // Parse existing cust_params (URL-encoded `k=v&k=v` *inside* the param value)
        const merged = {};
        if (existing) {
            for (const pair of existing.split('&')) {
                const [k, v = ''] = pair.split('=');
                if (k)
                    merged[decodeURIComponent(k)] = decodeURIComponent(v);
            }
        }
        // Add only keys not already present — never overwrite server-set values
        for (const [k, v] of Object.entries(extras)) {
            if (merged[k] === undefined)
                merged[k] = v;
        }
        const newCustParams = Object.entries(merged)
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&');
        url.searchParams.set('cust_params', newCustParams);
        return url.toString();
    }
    catch (_a) {
        // Malformed URL — leave it alone so IMA can surface its own error
        return vastUrl;
    }
}
